import * as React from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Typography,
} from "@mui/material";
import ReactMarkdown from "react-markdown";
import rehypeSanitize from "rehype-sanitize";
import { useTranslation } from "react-i18next";

import { useLegalPublished } from "@/api/legal";
import { usePrivacyChoices, useSetPrivacyChoice } from "@/api/privacy";
import { legalLangFor } from "@/features/legal/docs";

/** Asked before the chat sets cookies (spec §11.5, ePrivacy Art. 5(3)):
 *  the provider's notice, the cookies and the privacy text, with "Not
 *  now" and "Open chat" of equal weight. "Not now" stores nothing. */
export function ChatConsentDialog({
  open,
  onClose,
  onAccepted,
}: {
  open: boolean;
  onClose: () => void;
  onAccepted: () => void;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const uiLanguage = i18n.resolvedLanguage ?? i18n.language;
  const lang = legalLangFor(uiLanguage);
  const published = useLegalPublished();
  const privacy = usePrivacyChoices(open);
  const setChoice = useSetPrivacyChoice();
  const consent = privacy.data?.consent;
  const accept = () => {
    if (!consent) return;
    setChoice.mutate(
      {
        purpose: "support_chat_cookies",
        granted: true,
        text_hash: consent.hash,
        ui_language: uiLanguage,
        source: "chat_notice",
      },
      { onSuccess: onAccepted },
    );
  };
  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="chat-consent-title"
      // Portaled, but React bubbles its keydowns up the component tree:
      // from the phone's overflow menu, its MenuList would swallow them
      // (as in LanguageMenuButton). Escape still closes the dialog.
      onKeyDown={(e) => e.stopPropagation()}
    >
      <DialogTitle id="chat-consent-title">{t("support.consent.title")}</DialogTitle>
      <DialogContent>
        {privacy.data?.evaluation && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t("support.consent.evaluation", { place: privacy.data.location?.[lang] ?? "—" })}
          </Alert>
        )}
        {consent && <ReactMarkdown rehypePlugins={[rehypeSanitize]}>{consent.notice[lang]}</ReactMarkdown>}
        <ul>
          {consent?.cookies
            .filter((c) => c.class === "consent")
            .map((c) => (
              <li key={c.name}>
                <code>{c.name}</code> · {c.purpose[lang]} · {c.lifetime[lang]}
              </li>
            ))}
        </ul>
        {/* In evaluation mode nothing about Intercom is public (spec §11.9):
            the published Privacy Policy doesn't describe it, so the dialog
            carries the add-on's own privacy text (what is sent, the basis,
            retention, how to object) instead of linking there. */}
        {published && !privacy.data?.evaluation ? (
          <Link href="/legal/privacy" target="_blank" rel="noopener">
            {t("support.consent.privacy_link")}
          </Link>
        ) : (
          consent && <ReactMarkdown rehypePlugins={[rehypeSanitize]}>{consent.privacy_addon[lang]}</ReactMarkdown>
        )}
        <Typography variant="body2" sx={{ mt: 1 }}>
          {t("support.consent.email_instead")}
        </Typography>
        {setChoice.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {t("support.consent.error")}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          {t("support.consent.not_now")}
        </Button>
        <Button variant="outlined" disabled={setChoice.isPending || !consent} onClick={accept}>
          {t("support.consent.open_chat")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
